import {ClickCounter} from "../../../../../vet-agenda-actions/Resources/Private/JavaScript/Components/ClickCounter";
import {VideoPopup} from "../../../../../vet-agenda-actions/Resources/Private/JavaScript/Components/VideoPopup";
import {Bookmark} from "../../../../../vet-agenda-actions/Resources/Private/JavaScript/Components/Bookmark";
import {Rating} from "../../../../../vet-agenda-actions/Resources/Private/JavaScript/Components/Rating";
import {RatingActivator} from "../../../../../vet-agenda-actions/Resources/Private/JavaScript/Components/RatingActivator";
import {Comment} from "../../../../../vet-agenda-comments/Resources/Private/JavaScript/Components/Comment";

export class SearchOverlay {
    constructor() {
        this.overlay = document.querySelector('[data-search-overlay]');
        this.forms = Array.from(document.querySelectorAll('[data-search-form]'));

        if (!this.overlay || this.forms.length === 0) {
            return;
        }

        this.openClass = 'c-search-overlay--open';
        this.loadingClass = 'c-search-overlay__body--loading';
        this.dialog = this.overlay.querySelector('.c-search-overlay__dialog');
        this.intro = this.overlay.querySelector('.c-search-overlay__intro');
        this.overlayInput = this.overlay.querySelector('[data-search-overlay-input]');
        this.overlayForm = this.overlay.querySelector('[data-search-overlay-form]');
        this.overlayBody = this.overlay.querySelector('[data-search-overlay-body]');
        this.overlayContent = this.overlay.querySelector('[data-search-overlay-content]');
        this.overlaySummary = this.overlay.querySelector('[data-search-overlay-summary]');
        this.closeTriggers = this.overlay.querySelectorAll('[data-search-overlay-close]');
        this.clearTriggers = this.overlay.querySelectorAll('[data-search-clear]');
        this.activeRequest = null;

        this.setActiveForm(this.forms[0]);
        this.bindEvents();
    }

    setActiveForm(form) {
        this.form = form;
        this.barField = form.querySelector('.c-search-bar__field');
        this.formInput = form.querySelector('input[name$="[query]"], input[name="query"]');
        this.ajaxType = form.getAttribute('data-search-ajax-type') || '';
    }

    bindEvents() {
        this.forms.forEach((form) => {
            form.addEventListener('submit', (event) => {
                event.preventDefault();
                this.setActiveForm(form);
                this.open();
                this.fetchResults();
            });
        });

        if (this.overlayForm) {
            this.overlayForm.addEventListener('submit', (event) => {
                event.preventDefault();
                this.submitFromOverlay();
            });
        }

        this.closeTriggers.forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                event.preventDefault();
                this.close();
            });
        });

        this.clearTriggers.forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                event.preventDefault();
                this.clear();
            });
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isOpen()) {
                this.close();
            }
        });

        if (this.overlayContent) {
            this.overlayContent.addEventListener('change', (event) => {
                if (event.target.closest('[data-search-filter-input]')) {
                    this.form.querySelector('[data-search-force-year]')?.remove();
                    this.fetchResults();
                }
            });
        }
    }

    submitFromOverlay() {
        if (this.formInput && this.overlayInput) {
            this.formInput.value = this.overlayInput.value;
        }
        this.fetchResults();
    }

    isOpen() {
        return this.overlay.classList.contains(this.openClass);
    }

    open() {
        this.alignField();
        this.syncQuery();
        this.overlay.classList.add(this.openClass);
        this.overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('u-no-scroll');

        if (this.overlayInput) {
            window.requestAnimationFrame(() => this.overlayInput.focus());
        }
    }

    close() {
        this.clear();
        this.overlay.classList.remove(this.openClass);
        this.overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('u-no-scroll');

        if (this.formInput) {
            this.formInput.focus();
        }
    }

    clear() {
        this.overlayInput.value = '';
        this.formInput.value = '';
    }

    updateSummary() {
        if (!this.overlaySummary || !this.overlayContent) {
            return;
        }

        const count = this.overlayContent.querySelectorAll('.c-search-overlay__result').length;
        const pluralTemplate = this.overlaySummary.getAttribute('data-search-summary-template') || '%s Results for "%s"';
        const singularTemplate = this.overlaySummary.getAttribute('data-search-summary-template-singular') || pluralTemplate;
        const template = count === 1 ? singularTemplate : pluralTemplate;
        const query = this.getCurrentQuery();

        this.overlaySummary.innerHTML = this.formatSummary(template, [String(count), query]);
    }

    showSearchingSummary() {
        if (!this.overlaySummary) {
            return;
        }

        const template = this.overlaySummary.getAttribute('data-search-searching-template') || 'Searching for "%s"';

        this.overlaySummary.innerHTML = this.formatSummary(template, [this.getCurrentQuery()]);
    }

    getCurrentQuery() {
        return (this.overlayInput && this.overlayInput.value)
            || (this.formInput && this.formInput.value)
            || '*';
    }

    formatSummary(template, values) {
        let index = 0;
        return template.replace(/%s/g, () => (index < values.length ? values[index++] : ''));
    }

    syncQuery() {
        if (this.formInput && this.overlayInput) {
            this.overlayInput.value = this.formInput.value;
        }
    }

    buildResultsUrl() {
        const url = new URL(this.form.action, window.location.href);

        if (this.ajaxType !== '') {
            url.searchParams.set('type', this.ajaxType);
        }

        return url;
    }

    async fetchResults() {
        if (!this.overlayContent) {
            return;
        }

        if (this.activeRequest) {
            this.activeRequest.abort();
        }

        const url = this.buildResultsUrl();
        const controller = new AbortController();
        this.activeRequest = controller;
        if (this.overlayBody) {
            this.overlayBody.classList.add(this.loadingClass);
        }
        this.showSearchingSummary();

        try {
            const response = await fetch(url.toString(), {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'text/html',
                },
                body: new FormData(this.form),
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`Search request failed: ${response.status}`);
            }

            const html = await response.text();
            this.overlayContent.innerHTML = html;
            this.updateSummary();
        } catch (error) {
            if (error.name === 'AbortError') {
                return;
            }
            this.overlayContent.innerHTML = '';
        } finally {
            if (this.activeRequest === controller) {
                this.activeRequest = null;
            }
            if (this.overlayBody) {
                this.overlayBody.classList.remove(this.loadingClass);
            }

            new Bookmark();
            new ClickCounter();
            new Rating();
            new RatingActivator();
            new VideoPopup();
            new Comment();
        }
    }

    alignField() {
        if (!this.barField || !this.dialog) {
            return;
        }

        const fieldTop = this.barField.getBoundingClientRect().top;
        let introSpace = 0;

        if (this.intro) {
            const introStyles = window.getComputedStyle(this.intro);
            introSpace = this.intro.offsetHeight
                + parseFloat(introStyles.marginTop || 0)
                + parseFloat(introStyles.marginBottom || 0);
        }

        this.dialog.style.paddingTop = Math.max(0, fieldTop - introSpace) + 'px';
    }
}
